function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { Children, cloneElement, useCallback, useEffect, useRef, useState, FC, Ref, ElementType, CSSProperties, PropsWithChildren, ReactElement } from 'react';
import cn from 'classnames';
import { useForkRef } from '../../hooks/useForkRef';
import { useIsomorphicLayoutEffect } from '../../hooks/useIsomorphicLayoutEffect';
import { Tag, ITagProps } from '../Tag';
import { useResizeObserver } from '../../hooks/useResizeObserver';
import { useUniqId } from '../../hooks/useUniqId';
require("./Tags.css");
var styles = {
  "Root": "Tags-module-Root_U0o"
};
export const cnTags = cn('Tags');
const getCursorCut = ({
  childrenWidth = [],
  gap = 0,
  lineClamp = 2,
  rootWidth = 200,
  togglerWidth = 0
}) => {
  let cursor = 0;
  let fullWidth = 0;
  let hasToggler = false;
  childrenWidth.some((width, i, arr) => {
    const isLatestRow = Math.ceil((fullWidth + width) / rootWidth) >= lineClamp;
    if (isLatestRow && !hasToggler) {
      fullWidth = fullWidth + togglerWidth + gap;
      hasToggler = true;
    }
    const remainder = fullWidth / rootWidth % 1;
    const newWidth = remainder * rootWidth + width + gap;
    if (isLatestRow && newWidth > rootWidth) {
      return true;
    }
    let space = 0;
    if (newWidth + (childrenWidth.length !== i ? arr[i + 1] : 0) < rootWidth) {
      space = gap;
    } else if (remainder !== 0) {
      space = rootWidth - newWidth;
    }
    fullWidth = fullWidth + width + space;
    cursor = cursor + 1;
    return false;
  });
  return cursor;
};
const chevronRight20 = 'M8,13.4c-0.3,0.3-0.3,0.9,0,1.3c0.3,0.3,0.9,0.3,1.3,0L14,10L9.3,5.3C8.9,4.9,8.4,4.9,8,5.3C7.7,5.6,7.7,6.2,8,6.6l3.4,3.4L8,13.4z';
const chevronLeft20 = 'M9.4,10l3.4-3.4c0.3-0.3,0.3-0.9,0-1.3c-0.3-0.3-0.9-0.3-1.3,0L6.9,10l4.7,4.7c0.3,0.3,0.9,0.3,1.3,0c0.3-0.3,0.3-0.9,0-1.3L9.4,10z';
const initialDimentions = {
  childrenWidth: [],
  childrenHeight: 0,
  columnGap: 0,
  rootWidth: null,
  togglerWidth: 0
};
const DEFAULT_GAP = process.env.NODE_ENV === 'test' ? 8 : 0;
const DEFAULT_TOGGLER_WIDTH = process.env.NODE_ENV === 'test' ? 95 : 0;
const DEFAULT_COLLAPSED_SIZE = process.env.NODE_ENV === 'test' ? 64 : 0;
export const Tags = ({
  'aria-label': ariaLabel,
  children,
  className,
  controlRef,
  innerRef,
  lineClamp = 2,
  style = {},
  tag = 'div',
  ...props
}) => {
  const rootRef = useRef(null);
  const togglerRef = useRef(null);
  const collapsedSize = useRef(DEFAULT_COLLAPSED_SIZE);
  const dimentions = useRef(initialDimentions);
  const [isExtended, setIsExtended] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(true);
  const [cursorCut, setCursorCut] = useState(NaN);
  const handleRef = useForkRef(innerRef || controlRef, rootRef);
  const idMoreBtn = useUniqId('more-btn');
  const syncHeight = useCallback(() => {
    const root = rootRef.current;
    if (!root || !collapsedSize.current) return;
    if (collapsedSize.current < root.scrollHeight) {
      setHasOverflow(true);
    } else {
      setHasOverflow(false);
    }
    const {
      childrenWidth,
      columnGap: gap,
      togglerWidth
    } = dimentions.current;
    const cursor = getCursorCut({
      childrenWidth,
      gap,
      lineClamp,
      rootWidth: root.scrollWidth,
      togglerWidth
    });
    setCursorCut(cursor);
  }, [lineClamp]);
  const {
    width
  } = useResizeObserver({
    ref: rootRef,
    debounceDelay: 1000
  });
  useEffect(() => {
    syncHeight();
  }, [width, syncHeight, children]);
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof window === 'undefined') return;
    const computedStyleRoot = window.getComputedStyle(root);
    const rowGap = parseInt(computedStyleRoot.rowGap, 10) || DEFAULT_GAP;
    dimentions.current.togglerWidth = togglerRef.current?.offsetWidth || DEFAULT_TOGGLER_WIDTH;
    dimentions.current.columnGap = parseInt(computedStyleRoot.columnGap, 10) || DEFAULT_GAP;
    collapsedSize.current = dimentions.current.childrenHeight * lineClamp + (lineClamp - 1) * Number(rowGap);
    rootRef.current.style.height = root.scrollHeight > collapsedSize.current ? `${collapsedSize.current}px` : 'auto';
  }, [lineClamp]);
  useIsomorphicLayoutEffect(() => {
    syncHeight();
  }, []);
  const handleClick = useCallback(() => {
    setIsExtended(!isExtended);
    if (rootRef.current) {
      rootRef.current.style.height = !isExtended ? 'auto' : `${collapsedSize.current}px`;
    }
  }, [isExtended]);
  const Component = tag;
  const hiddenCount = Array.isArray(children) ? children.length - cursorCut : 0;
  dimentions.current.childrenWidth = [];
  return /*#__PURE__*/React.createElement(Component, _extends({
    role: "group",
    "aria-label": ariaLabel,
    style: {
      ...style
    },
    className: cn(styles.Root, className),
    ref: handleRef
  }, props), Children.map(children, (child, index) => {
    return /*#__PURE__*/cloneElement(child, {
      innerRef: ref => {
        if (ref) {
          dimentions.current.childrenHeight = ref.offsetHeight;
          dimentions.current.childrenWidth.push(ref.offsetWidth);
        }
      },
      ...(index >= cursorCut && !isExtended && {
        'aria-hidden': true
      })
    });
  }), /*#__PURE__*/React.createElement(Tag, {
    addonAfter: /*#__PURE__*/React.createElement("svg", {
      width: "20",
      height: "20",
      "aria-hidden": true,
      focusable: false
    }, /*#__PURE__*/React.createElement("path", {
      d: isExtended ? chevronLeft20 : chevronRight20
    })),
    "aria-hidden": !hasOverflow || hiddenCount === 0 || isNaN(hiddenCount),
    "aria-label": isExtended ? 'Скрыть' : undefined,
    id: idMoreBtn,
    innerRef: togglerRef,
    label: !isExtended ? `ещё ${hiddenCount}` : undefined,
    onClick: handleClick,
    size: "m",
    theme: "default",
    style: !hasOverflow ? {
      display: 'none'
    } : undefined
  }));
};
if (process.env.NODE_ENV !== 'production') {
  Tags.displayName = 'Tags';
}